/**
 * Custom side cart drawer — zastąpienie FunnelKit FKCart.
 *
 * DOM (raz w footerze, partials/side-cart.php):
 *   [data-hn-sidecart]        — root drawer (aria-hidden toggle)
 *   [data-hn-sidecart-body]   — kontener swapowany świeżym HTML z REST
 *   [data-hn-sidecart-count]  — licznik items w headerze
 *   [data-hn-cart-toggle]     — przycisk w nagłówku → otwórz
 *   [data-hn-sidecart-close]  — overlay + X → zamknij
 *
 * Wewnątrz body (re-binduje się po każdym swap'ie, bo użyty event delegation):
 *   [data-hn-cart-qty="inc|dec|set"] + data-key — zmiana qty
 *   [data-hn-cart-remove] + data-key            — usuwanie item
 *   [data-hn-cart-add] + value=product_id       — cross-sell add
 *
 * REST endpointy (PHP: inc/cart-rewards.php, localized do window.HN_SIDECART):
 *   urlGet                — GET pełny HTML body + count + total
 *   urlSet                — POST update qty / remove
 *   urlGet + '/add'       — POST add cross-sell product
 *
 * Listener na jQuery event `added_to_cart` (WC AJAX) — po dodaniu produktu
 * z karty / single product fetchujemy świeży HTML i otwieramy drawer.
 */

const cfg = () => window.HN_SIDECART || null;

let openTimer = null;

function getRoot() {
    return document.querySelector('[data-hn-sidecart]');
}

let lastTrigger = null;
let inertSiblings = [];

function setOpen(open) {
    const root = getRoot();
    if (!root) return;
    const wasOpen = root.classList.contains('is-open');
    if (open === wasOpen) return;
    if (open) lastTrigger = document.activeElement;
    root.toggleAttribute('inert', !open);
    root.setAttribute('aria-hidden', open ? 'false' : 'true');
    root.classList.toggle('is-open', open);
    document.body.classList.toggle('hn-sidecart-open', open);
    // Reszta strony nieaktywna, póki koszyk jest otwarty (WCAG 2.4.3); baner cookies zostaje dostępny.
    if (open) {
        inertSiblings = [...root.parentElement.children].filter((el) => el !== root && !el.inert
            && !el.matches('script, style, link, .cky-consent-container, .cky-modal'));
        inertSiblings.forEach((el) => { el.inert = true; });
    } else {
        inertSiblings.forEach((el) => { el.inert = false; });
        inertSiblings = [];
    }
    if (open) {
        const close = root.querySelector('.hn-sidecart__close');
        // Panel wchodzi z przejściem visibility, więc fokus dopiero po starcie animacji.
        setTimeout(() => close?.focus({ preventScroll: true }), 60);
    } else if (lastTrigger && document.contains(lastTrigger) && !lastTrigger.closest('[inert]')) {
        lastTrigger.focus({ preventScroll: true });
    }
}

function swapBody(html) {
    const body = document.querySelector('[data-hn-sidecart-body]');
    if (!body || typeof html !== 'string') return;

    // Preserve scroll position — `.hn-sidecart__body` ma `overflow-y:auto`, więc
    // innerHTML swap resetuje scrollTop do 0 (skok do góry po dodaniu cross-sella
    // z dołu drawera). Zapisujemy przed, przywracamy po nowym render.
    const prevScroll = body.scrollTop;
    body.innerHTML = html;
    // Restore w następnej klatce — dajemy przeglądarce zlayoutować nowy content
    // żeby scrollHeight był prawidłowy.
    requestAnimationFrame(() => {
        // Klamruj do nowego scrollHeight (jeśli content się skrócił, np. po remove).
        const max = Math.max(0, body.scrollHeight - body.clientHeight);
        body.scrollTop = Math.min(prevScroll, max);
    });
}

function setCount(count) {
    const el = document.querySelector('[data-hn-sidecart-count]');
    if (el) el.textContent = String(count);
}

/**
 * Custom event `hn:sidecart:refresh` na document — pozwala innym modułom
 * (np. cross-sell upsell po add) wymusić odświeżenie bez open/close.
 */
async function fetchFresh() {
    const c = cfg();
    if (!c?.urlGet) return null;
    try {
        const res = await fetch(c.urlGet, {
            credentials: 'same-origin',
            headers: { 'X-WP-Nonce': c.nonce, 'Accept': 'application/json' },
        });
        if (!res.ok) return null;
        const data = await res.json();
        if (data?.html) swapBody(data.html);
        if (data && 'count' in data) setCount(data.count);
        return data;
    } catch (_) {
        return null;
    }
}

async function postAction(url, body) {
    const c = cfg();
    if (!c?.nonce) return null;
    try {
        const fd = new FormData();
        Object.entries(body).forEach(([k, v]) => fd.append(k, v));
        const res = await fetch(url, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'X-WP-Nonce': c.nonce, 'Accept': 'application/json' },
            body: fd,
        });
        if (!res.ok) return null;
        const data = await res.json();
        if (data?.html) swapBody(data.html);
        if (data && 'count' in data) setCount(data.count);

        // Update header mini cart directly — bez `wc_fragment_refresh` trigger,
        // bo WC fragments filter `woocommerce_add_to_cart_fragments` w
        // inc/cart-rewards.php zawiera też `[data-hn-sidecart-body]` z replaceWith,
        // co po naszym swapBody robi DRUGI render i "flash reload" w panelu.
        // Zamiast tego patchujemy te same elementy które WC by patchował.
        if (data && 'count' in data) {
            document.querySelectorAll('[data-hn-header-cart-count]').forEach((el) => {
                el.textContent = String(data.count);
                el.classList.toggle('is-empty', data.count <= 0);
            });
        }
        if (data && 'total' in data) {
            // `data.total` z PHP `wp_strip_all_tags($cart->get_total())` zawiera HTML
            // entity (&nbsp;, &#122;=z, &#322;=ł). textContent renderuje je dosłownie,
            // innerHTML dekoduje. Source = wc_price() — trusted, no XSS risk.
            document.querySelectorAll('[data-hn-header-cart-total]').forEach((el) => {
                el.innerHTML = data.total;
            });
        }

        // Custom event dla 3rd party listenerów (B2BKing, AutomateWoo widgets) —
        // emituje informację o zmianie koszyka bez triggera fragment refresh.
        document.dispatchEvent(new CustomEvent('hn:cart:updated', { detail: data }));
        if (window.jQuery && data) {
            window.jQuery(document.body).trigger('hn_cart_updated', [data]);
        }
        return data;
    } catch (_) {
        return null;
    }
}

function bindDelegatedHandlers() {
    const root = getRoot();
    if (!root || root.dataset.hnBound === '1') return;
    root.dataset.hnBound = '1';

    const c = cfg();
    if (!c) return;

    // Close — overlay + X
    root.addEventListener('click', (e) => {
        if (e.target.closest('[data-hn-sidecart-close]')) {
            e.preventDefault();
            setOpen(false);
        }
    });

    // Rewards toggle — "Zobacz wszystkie nagrody" (mobile only, CSS toggle via
    // `.is-expanded` na `.hn-sidecart__rewards`). Re-bind nie potrzebny bo
    // event delegation na rootcie łapie nawet po body swap'ie.
    root.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-hn-rewards-toggle]');
        if (!btn) return;
        e.preventDefault();
        const wrap = btn.closest('.hn-sidecart__rewards');
        if (!wrap) return;
        const expanded = wrap.classList.toggle('is-expanded');
        btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });

    // Qty + remove + cross-sell add (event delegation, działa po każdym swap'ie)
    root.addEventListener('click', async (e) => {
        const qtyBtn = e.target.closest('[data-hn-cart-qty]');
        // UWAGA: input qty ma `data-hn-cart-qty="set"`, więc też pasuje do tego
        // selektora. Klikanie w input ma być no-op (request leci dopiero w
        // `change` handlerze niżej). Łapiemy tylko inc/dec buttony.
        if (qtyBtn && (qtyBtn.dataset.hnCartQty === 'inc' || qtyBtn.dataset.hnCartQty === 'dec')) {
            e.preventDefault();
            const action = qtyBtn.dataset.hnCartQty;
            const key = qtyBtn.dataset.key;
            if (!key) return;

            const input = root.querySelector(`[data-hn-cart-qty="set"][data-key="${key}"]`);
            // Skok o krok B2BKing (np. 6), klamrowany do min/max z inputu (stock-aware).
            const step = input ? Math.max(1, parseInt(input.step, 10) || 1) : 1;
            const min = input ? Math.max(1, parseInt(input.min, 10) || 1) : 1;
            const maxAttr = input ? parseInt(input.max, 10) : NaN;
            const max = (Number.isFinite(maxAttr) && maxAttr > 0) ? maxAttr : Infinity;
            const current = input ? (parseInt(input.value, 10) || min) : min;
            let qty = action === 'inc' ? current + step : current - step;
            if (qty < min) qty = min;
            if (qty > max) qty = max;

            // Optimistic: zaktualizuj input żeby user widział nową wartość zanim
            // serwer zwróci fresh HTML.
            if (input) input.value = String(qty);

            await postAction(c.urlSet, { key, qty, action_type: '' });
            return;
        }

        const removeBtn = e.target.closest('[data-hn-cart-remove]');
        if (removeBtn) {
            e.preventDefault();
            const key = removeBtn.dataset.key;
            if (!key) return;
            await postAction(c.urlSet, { key, action_type: 'remove' });
            return;
        }

        // Freebie picker — wybór produktu-nagrody z tier'a progowego.
        const freebieBtn = e.target.closest('[data-hn-freebie-pick]');
        if (freebieBtn) {
            e.preventDefault();
            const remove = freebieBtn.classList.contains('is-active'); // ponowne kliknięcie cofa wybór
            const productId = parseInt(freebieBtn.dataset.hnFreebiePick, 10);
            const tierKey = freebieBtn.dataset.tierKey;
            if (!productId || !tierKey) return;
            freebieBtn.classList.add('is-loading');
            freebieBtn.setAttribute('aria-busy', 'true');
            const urlFreebie = c.urlGet.replace(/\/side-cart\/?$/, '/side-cart/freebie');
            try {
                await postAction(urlFreebie, { tier_key: tierKey, product_id: productId, remove });
            } finally {
                freebieBtn.classList.remove('is-loading');
                freebieBtn.removeAttribute('aria-busy');
            }
            return;
        }

        const addBtn = e.target.closest('[data-hn-cart-add]');
        if (addBtn) {
            e.preventDefault();
            const productId = parseInt(addBtn.dataset.hnCartAdd, 10);
            if (!productId) return;
            // Loader feedback — class triggeruje spinner w CSS + pointer-events:none.
            addBtn.classList.add('is-loading');
            addBtn.setAttribute('aria-busy', 'true');
            const urlAdd = c.urlGet.replace(/\/side-cart\/?$/, '/side-cart/add');
            try {
                await postAction(urlAdd, { product_id: productId, qty: 1 });
            } finally {
                // postAction re-renderuje side-cart body, więc oryginalny addBtn jest
                // poza DOM-em — ale na wypadek gdyby został (np. error przed re-render)
                // usuń klasę żeby user mógł spróbować jeszcze raz.
                addBtn.classList.remove('is-loading');
                addBtn.removeAttribute('aria-busy');
            }
            return;
        }
    });

    // Qty input — `change` event (manualnie wpisana wartość). Floor=1 — pusta
    // wartość / NaN / 0 nie usuwa pozycji (do tego służy przycisk Usuń). Snap
    // input z powrotem do clean number jeśli user wpisał śmieci.
    root.addEventListener('change', async (e) => {
        const input = e.target.closest('[data-hn-cart-qty="set"]');
        if (!input) return;
        const key = input.dataset.key;
        if (!key) return;

        // Dosuń ręcznie wpisaną wartość do reguły B2BKing (min + wielokrotność kroku), max stock-aware.
        const step = Math.max(1, parseInt(input.step, 10) || 1);
        const min = Math.max(1, parseInt(input.min, 10) || 1);
        const maxAttr = parseInt(input.max, 10);
        const max = (Number.isFinite(maxAttr) && maxAttr > 0) ? maxAttr : Infinity;
        let qty = parseInt(input.value, 10);
        if (!Number.isFinite(qty) || qty < min) qty = min;
        else if (step > 1 && qty % step !== 0) qty = Math.ceil(qty / step) * step;
        if (qty > max) { qty = Math.floor(max / step) * step; if (qty < min) qty = min; }

        // Snap UI back gdyby user wpisał '0', '' lub coś niepoprawnego / nie-wielokrotność.
        if (String(qty) !== input.value) {
            input.value = String(qty);
        }

        await postAction(c.urlSet, { key, qty, action_type: '' });
    });
}

function bindToggles() {
    document.addEventListener('click', (e) => {
        const t = e.target.closest('[data-hn-cart-toggle]');
        if (!t) return;
        e.preventDefault();
        // Świeży snapshot przy każdym otwarciu (state mógł się zmienić w innej karcie).
        fetchFresh();
        setOpen(true);
    });

    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') setOpen(false);
    });
}

/**
 * WC AJAX add-to-cart event signature: (event, fragments, cart_hash, button)
 *
 * Fragments (PHP filter `woocommerce_add_to_cart_fragments` w cart-rewards.php)
 * mają już świeży HTML side carta — selektor `div[data-hn-sidecart-body]`.
 * Czytamy go bezpośrednio, **bez** dodatkowego GET /hn/v1/side-cart, co
 * eliminuje opóźnienie ~200-500ms między klikiem a otwarciem drawer'a.
 *
 * WC sam zrobi `replaceWith()` dla wszystkich fragmentów przed event'em,
 * więc kiedy nasz callback się odpala — `[data-hn-sidecart-body]` w DOM
 * jest już świeży. Otwieramy drawer od razu.
 *
 * Fallback do fetchFresh() — gdyby fragment nie został podpięty (race,
 * inny plugin nadpisał filtr, edge case).
 */
function bindAddedToCart() {
    if (!window.jQuery) return;
    window.jQuery(document.body).on('added_to_cart', (event, fragments) => {
        clearTimeout(openTimer);
        openTimer = setTimeout(() => {
            const hasFragment = fragments && fragments['div[data-hn-sidecart-body]'];
            if (hasFragment) {
                // WC już swap'nął HTML w DOM. Otwórz od razu, bez fetcha.
                setOpen(true);
            } else {
                fetchFresh().then(() => setOpen(true));
            }
        }, 0);
    });
}

/**
 * Hydrate header mini-cart count/total z REST API.
 *
 * Powód: nginx fastcgi cache trzyma blog/kategorie 24h dla guest'ów. HTML
 * mini-cart (count + total) jest częścią cachowanego renderu — wszyscy
 * guest'owie widzieliby ten sam stan (z momentu cache fillu). Bez hydratacji
 * user dodaje produkt, idzie na cachowaną stronę → znów widzi "0".
 *
 * Strategy: PHP renderuje placeholder (0 / "") dla guest'ów, JS po
 * DOMContentLoaded fetchuje aktualny stan z `/hn/v1/side-cart` i populuje
 * `[data-hn-header-cart-count]` + `[data-hn-header-cart-total]`.
 *
 * Skip:
 *  - Brak elementów w DOM (np. footer-only pages)
 *  - Brak HN_SIDECART config (REST URLs nie wstrzyknięte)
 *  - Logged-in users (PHP już zrenderował realny stan — BYPASS cache w nginx)
 */
async function hydrateHeaderCart() {
    const countEls = document.querySelectorAll('[data-hn-header-cart-count]');
    const totalEls = document.querySelectorAll('[data-hn-header-cart-total]');
    if (!countEls.length && !totalEls.length) return;

    const c = cfg();
    if (!c || !c.urlGet) return;

    // Body class hn-logged-in === wp-admin-bar present === user zalogowany.
    // Wtedy PHP wyrenderował realny stan, hydrate niepotrzebny.
    if (document.body.classList.contains('logged-in')) return;

    try {
        // Lekki endpoint: tylko count+total (bez renderu body / cross-sell WP_Query).
        const urlSummary = c.urlGet.replace(/\/side-cart\/?$/, '/side-cart/summary');
        const res = await fetch(urlSummary, {
            method: 'GET',
            credentials: 'same-origin',
            headers: { 'X-WP-Nonce': c.nonce, 'Accept': 'application/json' },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!data) return;
        if ('count' in data) {
            countEls.forEach((el) => {
                el.textContent = String(data.count);
                el.classList.toggle('is-empty', data.count <= 0);
            });
        }
        if ('total' in data && data.total) {
            // wc_price() output — trusted, no XSS risk.
            totalEls.forEach((el) => { el.innerHTML = data.total; });
        }
    } catch (_) {
        // Silent fail — header pokazuje stale placeholder zamiast crash.
    }
}

export function initSideCart() {
    if (!getRoot() || !cfg()) return;
    bindDelegatedHandlers();
    bindToggles();
    bindAddedToCart();
    hydrateHeaderCart();
}
